// BEGIN /src/features/account/conversations.slice.tsx:
import { createEntityAdapter, createSlice, createAsyncThunk, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { API_ROUTES } from 'src/constants/routes';
import { httpClient } from 'src/libs';
import { selectContactById } from './contacts.slice';
import { RootState } from 'src/store';
import { mergeExistingOrNewConversationAndGetUpdatedConversationObject } from 'src/utils/reducerHelpers';
import { FollowUpSettings } from 'src/types/followUpSettings';

// Helper function to sort messages by timestamp priority
// if this is better to add some sort time param to the get endpoint on comms-api conversations_api.rb we can do this
const sortMessagesByTimestampPriority = (messages: Message[]): Message[] => {
  if (!messages || !Array.isArray(messages)) return [];

  return [...messages].map(msg => {
    // Determine which timestamp was used for sorting
    const sortTimeUsed = msg.delivered_at || msg.deliver_after || msg.created_at;
    return { ...msg, sort_time_used: sortTimeUsed };
  }).sort((a, b) => {
    // Sort by delivered_at if available, otherwise by deliver_after, then created_at
    const aTime = a.delivered_at ? new Date(a.delivered_at).getTime() :
      a.deliver_after ? new Date(a.deliver_after).getTime() :
        new Date(a.created_at).getTime();

    const bTime = b.delivered_at ? new Date(b.delivered_at).getTime() :
      b.deliver_after ? new Date(b.deliver_after).getTime() :
        new Date(b.created_at).getTime();

    return aTime - bTime;
  });
};

export interface Message {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  status: string;
  subject: string;
  call_status: string;
  deliver_after: string;
  conversation_id: string;
  sender_role: string;
  sender_name: string;
  memory: Record<string, any>;
  raw_content: string | null;
  delivered_at: string;
  hide_from_context: boolean;
  marked_to_be_deleted: boolean;
  metadata: Record<string, any>;
  sort_time_used: string | null;
  feedbacks?: any[];
}

export interface Entity {
  id: string;
  name: string;
  identifier?: string;
  role: string;
  tag?: string;
  contact?: any;
  contact_id?: string;
}

export interface Conversation {
  id: string;
  channel_id: string;
  status: string;
  subject: string;
  call_status: string;
  latest_message_at: string;
  latest_delivery_at: string;
  delivery_state: string;
  delivery_delay: number;
  next_autocomplete_scheduled_at: string;
  retries: number;
  participating_entities: Entity[];
  memory: Record<string, any>;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  messages: Message[];
  autocomplete_status: string;
  read: boolean;
  message_count: number;
  tags?: string[];
  loading: boolean;
  error: any;
  account_id: string;
  follow_up: FollowUpSettings;
  user_messages_count?: number;
  last_user_message_received_at?: string;
  delivered_assistant_messages_since_last_user_message?: number;
  starred?: boolean;
  _hasFullData?: boolean;
  total_messages_count?: number; // Total messages in conversation (from serializer)
}

const conversationsAdapter = createEntityAdapter<Conversation>({
  selectId: (conversation) => conversation.id,
  sortComparer: (a, b) => {
    // Safely compare the latest activity timestamps of two conversations.
    const getTimestamp = (conversation: Conversation): number => {
      // First try to use latest_delivery_at, then fall back to latest_message_at, then updated_at
      const dateString = conversation.latest_delivery_at || conversation.latest_message_at || conversation.updated_at;
      return dateString ? new Date(dateString).getTime() : 0;
    };

    return getTimestamp(b) - getTimestamp(a);
  },
});

// Extending EntityState to include additional properties
interface ConversationsState extends EntityState<Conversation> {
  loading: boolean;
  error: any;
  initialLoad: boolean;
  openConversationId: string | null;
  totalConversations: number;
  hasMore: boolean;
  lastCursor: string | null;
  limit: number;
}

const initialState: ConversationsState = conversationsAdapter.getInitialState({
  loading: false,
  error: null,
  initialLoad: false,
  openConversationId: null,
  totalConversations: 0,
  hasMore: false,
  lastCursor: null,
  limit: 25
});

interface FetchConversationsParams {
  conversationId?: string;
  status?: string;
  subjectContains?: string;
  queryString?: string;
  participatingEntityIdentifiers?: string[];
  contactId?: string;
  memory_contains?: string | object;
  conversationLatestDeliveryAtBefore?: Date;
  conversationLatestMessageCreatedAtBefore?: Date;
  conversationLastUpdatedBefore?: Date;
  limit?: number;
  messagesLimit?: number;
  cursor?: string | null;
  channel_id?: string;
  user_not_responded_to?: boolean;
  has_draft_messages?: boolean;
  has_engaged_users?: boolean;
  has_scheduled_delivery?: boolean;
  delivery_state?: string;
  is_priority?: boolean;
  full_payload?: boolean;
  universal_search?: string;
  tags?: string[] | string;
}

interface FetchConversationWithMessagesParams {
  conversationId: string;
  messagesLimit?: number;
}

export const fetchConversationsWithMessages = createAsyncThunk(
  'conversations/fetchConversationsWithMessages',
  async (params: FetchConversationsParams = {}) => {
    const searchParams = new URLSearchParams();
    
    // Always append full_payload parameter
    searchParams.append('full_payload', String(params.full_payload !== false));
    
    // Add limit and messages_limit with defaults
    searchParams.append('limit', String(params.limit || 25));
    searchParams.append('messages_limit', String(params.messagesLimit || 3));
    searchParams.append('status', params.status || 'all_active');
    
    if (params.conversationId) searchParams.append('id', params.conversationId);
    if (params.subjectContains) searchParams.append('subject_contains', params.subjectContains);
    if (params.queryString) searchParams.append('query_string', params.queryString);
    if (params.participatingEntityIdentifiers) searchParams.append('participating_entity_identifiers', JSON.stringify(params.participatingEntityIdentifiers));
    if (params.contactId) searchParams.append('contact_id', params.contactId);
    if (params.memory_contains) {
      if (typeof params.memory_contains === 'string') {
        searchParams.append('memory_contains', params.memory_contains);
      } else {
        searchParams.append('memory_contains', JSON.stringify(params.memory_contains));
      }
    }
    if (params.channel_id) searchParams.append('channel_id', params.channel_id);
    if (params.user_not_responded_to) searchParams.append('user_not_responded_to', String(params.user_not_responded_to));
    if (params.has_draft_messages) searchParams.append('has_draft_messages', String(params.has_draft_messages));
    if (params.has_engaged_users) searchParams.append('has_engaged_users', String(params.has_engaged_users));
    if (params.has_scheduled_delivery) searchParams.append('has_scheduled_delivery', String(params.has_scheduled_delivery));
    if (params.delivery_state) searchParams.append('delivery_state', params.delivery_state);
    if (params.conversationLatestDeliveryAtBefore) searchParams.append('conversation_latest_delivery_at_before', params.conversationLatestDeliveryAtBefore.toISOString());
    if (params.conversationLatestMessageCreatedAtBefore) searchParams.append('conversation_latest_message_created_at_before', params.conversationLatestMessageCreatedAtBefore.toISOString());
    if (params.conversationLastUpdatedBefore) searchParams.append('conversation_last_updated_before', params.conversationLastUpdatedBefore.toISOString());
    if (params.is_priority) searchParams.append('is_priority', String(params.is_priority));
    if (params.universal_search) searchParams.append('universal_search', params.universal_search);

    // Handle tags filter - can be array or comma-separated string
    if (params.tags) {
      if (Array.isArray(params.tags)) {
        searchParams.append('tags', params.tags.join(','));
      } else {
        searchParams.append('tags', params.tags);
      }
    }

    // Handle cursor parameter - map to the appropriate backend parameter
    if (params.cursor) {
      // The backend expects one of the timestamp-based cursor parameters
      // We'll use conversation_latest_delivery_at_before as the primary cursor
      searchParams.append('conversation_latest_delivery_at_before', params.cursor);
    }

    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/?${searchParams.toString()}`;
    const response = await httpClient.get(url);
    return response.data;
  }
)

// Track recent fetch requests to prevent duplicates
const recentFetchRequests = new Map<string, number>();

export const fetchConversationWithMessages = createAsyncThunk(
  'conversations/fetchConversationWithMessages',
  async ({ conversationId, messagesLimit = 30 }: FetchConversationWithMessagesParams, { getState }) => {
    // Check for valid conversationId
    if (!conversationId || conversationId === 'null' || conversationId === 'undefined') {
      throw new Error('Invalid conversation ID');
    }

    // Create a unique key for this specific request
    const requestKey = `${conversationId}_${messagesLimit}`;
    const now = Date.now();
    const lastRequest = recentFetchRequests.get(requestKey);

    // Skip if we've made the same request within the last 300ms
    if (lastRequest && now - lastRequest < 300) {
      const state = getState() as RootState;
      const existingConversation = state.conversations.entities[conversationId];
      if (existingConversation) {
        return existingConversation;
      }
    }

    // Update the request timestamp
    recentFetchRequests.set(requestKey, now);

    // Clean up old entries periodically
    if (recentFetchRequests.size > 100) {
      const cutoff = now - 5000;
      for (const [key, timestamp] of recentFetchRequests.entries()) {
        if (timestamp < cutoff) {
          recentFetchRequests.delete(key);
        }
      }
    }

    // Check if we already have this conversation with enough messages loaded
    const state = getState() as RootState;
    const existingConversation = state.conversations.entities[conversationId];
    if (existingConversation &&
      existingConversation.messages &&
      messagesLimit > 0 && // Only check message count if we're actually requesting messages
      existingConversation.messages.length >= messagesLimit &&
      !existingConversation.loading) {
      return existingConversation;
    }

    const queryParams = new URLSearchParams({
      messages_limit: messagesLimit.toString(),
    }).toString();
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}?${queryParams}`;
    const response = await httpClient.get(url);
    const conversation = response.data
    return conversation;
  }
);


export const selectConversationsWithContacts = (state: RootState): Conversation[] => state.conversations.ids.map(id => {
  const conversation = selectConversationById(state, id);
  if (!conversation) return null; // Ensure conversation exists
  return {
    ...conversation,
    participating_entities: conversation.participating_entities ? conversation.participating_entities.map(entity => {
      // Handle both object and string formats
      if (typeof entity === 'string') {
        // Return a minimal valid Entity object
        return { 
          id: '', // We don't have an ID for string entities
          identifier: entity, 
          name: entity,
          role: 'unknown' // We don't know the role
        } as Entity;
      }
      const contact = entity.contact_id ? selectContactById(state, entity.contact_id) : null;
      return {
        ...entity,
        contact: contact ? { ...contact, initialLoad: true, loading: false } : (entity.contact_id ? { id: entity.contact_id, initialLoad: false, loading: true } : null)
      };
    }) : []
  };
}).filter((conversation): conversation is Conversation => conversation !== null); // Filter out null entries with type guard

export const updateConversations = (conversations) => ({
  type: 'conversations/update',
  payload: conversations
});

export const processPusherConversationUpdated = createAsyncThunk(
  'conversations/processPusherConversationUpdated',
  async (data: { object: any }, thunkAPI) => {
    return data.object;
  }
)

export const processPusherConversationCreated = createAsyncThunk(
  'conversations/processPusherConversationCreated',
  async (data: { object: any }, thunkAPI) => {
    return data.object;
  }
)

export const processPusherConversationDeleted = createAsyncThunk(
  'conversations/processPusherConversationDeleted',
  async (data: { object: any }, thunkAPI) => {
    return data.object;
  }
)

export const updateConversationStatus = createAsyncThunk(
  'conversations/updateStatus',
  async ({ conversationId, status, channelId }: { conversationId: string, status: string, channelId: string }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}`;
    const body = { status: status };
    const response = await httpClient.patch(url, body);
    return response.data;
  }
)

export const conversationAutocomplete = createAsyncThunk(
  'conversations/conversationAutocomplete',
  async ({ channelId, conversationId }: { channelId: string; conversationId: string }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/autocomplete`;
    const body = { channelId };
    const response = await httpClient.post(url, body);
    return response.data;
  }
)

export const conversationAutocompleteNow = createAsyncThunk(
  'conversations/conversationAutocompleteNow',
  async ({ channelId, conversationId }: { channelId: string; conversationId: string }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/autocomplete_now`;
    const body = { channelId };
    const response = await httpClient.post(url, body);
    return response.data;
  }
)

export const updateConversationDeliveryDelay = createAsyncThunk(
  'conversations/updateDeliveryDelay',
  async ({ conversationId, delivery_delay, channelId, status }: { conversationId: string, delivery_delay: number, channelId: string, status: string }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}`;
    const body = { delivery_delay: delivery_delay };
    const response = await httpClient.patch(url, body);
    return response.data;
  }
)

export const updateConversationDeliveryState = createAsyncThunk(
  'conversations/updateDeliveryState',
  async ({ conversationId, deliveryState, channelId }: { conversationId: string, deliveryState: string, channelId: string }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}`;
    const body = { delivery_state: deliveryState };
    const response = await httpClient.patch(url, body);
    return response.data;
  }
)

export const fetchMessages = createAsyncThunk(
  'conversations/fetchMessages',
  async ({ conversationId, channelId }: { conversationId: string; channelId: string }) => {
    const url = `${process.env.REACT_APP_API_V3_URL}${API_ROUTES.chat_message_thread}?channel_id=${channelId}&conversation_id=${conversationId}`;
    const response = await httpClient.get(url);
    return response.data;
  }
)

export const sendMessage = createAsyncThunk(
  'conversations/sendMessage',
  async ({ conversation, message, sendingEntity, mediaUrls }: {
    conversation: any;
    message: string;
    sendingEntity: any;
    mediaUrls?: string[];
  }) => {
    try {
      if (message == null || message === '') {
        throw new Error('Message cannot be empty.');
      }
      // Use v2 endpoint to support media_urls like BulkConversationCreateDialog
      const url = `${process.env.REACT_APP_API_V3_URL}/chat/private/messages/post_message_v2`;

      // Base required payload
      const data: any = {
        conversation_id: conversation?.id,
        content: message,
        from_role: sendingEntity?.role, // role is always required
        channel_id: conversation.channel_id,
      };

      // Only include identifier & name for non-function roles to prevent backend errors
      if (sendingEntity?.role !== 'function') {
        if (sendingEntity?.identifier) data.from_identifier = sendingEntity.identifier;
        if (sendingEntity?.name)       data.from_name       = sendingEntity.name;
      }

      // Add media_urls if provided (matching BulkConversationCreateDialog)
      if (mediaUrls && mediaUrls.length > 0) {
        data.media_urls = mediaUrls;
      }

      const response = await httpClient.post(url, data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }
)

export const initiateCall = createAsyncThunk(
  'conversations/initiateCall',
  async ({ channelId, fromOppyPhoneNumber, toAdminPhoneNumber, connectToNumber, callerName, contactName, conversationId }: {
    channelId: string;
    fromOppyPhoneNumber: string;
    toAdminPhoneNumber: string;
    connectToNumber: string;
    callerName: string;
    contactName: string;
    conversationId?: string;
  }) => {
    const url = `${process.env.REACT_APP_API_V3_URL}${API_ROUTES.private_channel}/${channelId}/initiate_call`;
    const response = await httpClient.post(url, {
      from_oppy_number: fromOppyPhoneNumber,
      to_admin_number: toAdminPhoneNumber,
      connect_to_number: connectToNumber,
      caller_name: callerName,
      contact_name: contactName,
      conversation_id: conversationId
    });
    return {
      success: response.data.success,
      conversation_id: response.data.conversation_id,
      ...response.data
    };
  }
);

export const updateConversation = createAsyncThunk(
  'conversations/updateConversation',
  async (
    {
      conversationId,
      channelId,
      conversationData,
    }: { conversationId: string; channelId: string; conversationData: Partial<Conversation> },
    thunkAPI
  ) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}`;
    const body = {
      ...conversationData
    };
    // // extract each key from the body and put in an array
    // const keys = Object.keys(body);
    // // log the keys
    // console.log("conversationData updateConversation to run for keys", keys);
    const response = await httpClient.patch(url, body);
    return response.data.conversation;
  }
);

export const updateConversationTags = createAsyncThunk(
  'conversations/updateTags',
  async (
    {
      conversationId,
      tags,
    }: { conversationId: string; tags: string[] },
    thunkAPI
  ) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}`;
    const body = { tags };
    const response = await httpClient.patch(url, body);
    return response.data.conversation;
  }
);

export const toggleConversationStarred = createAsyncThunk(
  'conversations/toggleStarred',
  async (
    {
      conversationId,
      starred,
    }: { conversationId: string; starred: boolean },
    thunkAPI
  ) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}`;
    const body = { starred };
    const response = await httpClient.patch(url, body);
    return response.data.conversation;
  }
);

export const getConversationFollowUp = createAsyncThunk(
  'conversations/getFollowUp',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/follow_up`;
      const response = await httpClient.get(url);
      return {
        conversationId,
        followUp: response.data.follow_up
      };
    } catch (error) {
      console.error('Error fetching follow-up settings:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch follow-up settings');
    }
  }
);

export const updateConversationFollowUp = createAsyncThunk(
  'conversations/updateFollowUp',
  async (
    {
      conversationId,
      followUpSettings,
    }: { conversationId: string; followUpSettings: FollowUpSettings },
    { rejectWithValue }
  ) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/follow_up`;

      // Handle each property individually to ensure proper null/undefined handling
      const formData = new URLSearchParams();

      // Handle each property individually to ensure proper null/undefined handling
      if (followUpSettings.enabled !== undefined) {
        formData.append('enabled', followUpSettings.enabled.toString());
      }

      if (followUpSettings.delay_minutes !== undefined) {
        formData.append('delay_minutes', followUpSettings.delay_minutes.toString());
      }

      if (followUpSettings.prompt !== undefined) {
        formData.append('prompt', followUpSettings.prompt);
      }

      if (followUpSettings.respect_business_hours !== undefined) {
        formData.append('respect_business_hours', followUpSettings.respect_business_hours.toString());
      }

      // Handle max_message_attempts specially - send 'null' as string when it's null
      if (followUpSettings.max_message_attempts !== undefined) {
        if (followUpSettings.max_message_attempts === null) {
          formData.append('max_message_attempts', 'null');
        } else {
          formData.append('max_message_attempts', followUpSettings.max_message_attempts.toString());
        }
      }

      if (followUpSettings.schedule_type !== undefined) {
        formData.append('schedule_type', followUpSettings.schedule_type);
      }

      if (followUpSettings.skip_weekends !== undefined) {
        formData.append('skip_weekends', followUpSettings.skip_weekends.toString());
      }

      if (followUpSettings.limit_follow_up_attempts !== undefined) {
        formData.append('limit_follow_up_attempts', followUpSettings.limit_follow_up_attempts.toString());
      }

      const response = await httpClient.patch(url, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      return {
        conversationId,
        followUp: response.data.follow_up
      };
    } catch (error) {
      console.error('Error updating follow-up settings:', error);
      return rejectWithValue(error.response?.data || 'Failed to update follow-up settings');
    }
  }
);

export const enableConversationFollowUp = createAsyncThunk(
  'conversations/enableFollowUp',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/follow_up/enable`;
      const response = await httpClient.post(url);
      return {
        conversationId,
        followUp: response.data.follow_up
      };
    } catch (error) {
      console.error('Error enabling follow-up:', error);
      return rejectWithValue(error.response?.data || 'Failed to enable follow-up');
    }
  }
);

export const disableConversationFollowUp = createAsyncThunk(
  'conversations/disableFollowUp',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/follow_up/disable`;
      const response = await httpClient.post(url);
      return {
        conversationId,
        followUp: response.data.follow_up
      };
    } catch (error) {
      console.error('Error disabling follow-up:', error);
      return rejectWithValue(error.response?.data || 'Failed to disable follow-up');
    }
  }
);

export const deleteConversations = createAsyncThunk(
  'conversations/deleteConversations',
  async (conversationIds: string[], { rejectWithValue }) => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/bulk_delete`;
      const response = await httpClient.post(url, { conversation_ids: conversationIds });

      if (response.data.success) {
        return {
          successfulDeletes: response.data.successful_deletes,
          failedDeletes: response.data.failed_deletes.map(f => f.id)
        };
      } else {
        return rejectWithValue('Failed to delete conversations');
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete conversations');
    }
  }
);

// New thunk to fetch full data for specific conversations
export const fetchConversationsFullData = createAsyncThunk(
  'conversations/fetchConversationsFullData',
  async (conversationIds: string[]) => {
    const searchParams = new URLSearchParams();
    searchParams.append('full_payload', 'true');
    // Don't set a limit when fetching specific conversations by ID - the backend will return all requested
    searchParams.append('messages_limit', '3');
    
    // Add conversation IDs to the query
    conversationIds.forEach(id => searchParams.append('conversation_ids[]', id));
    
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations?${searchParams.toString()}`;
    const response = await httpClient.get(url);
    
    return response.data;
  }
);

// Add new thunks for function execution
export const listConversationFunctions = createAsyncThunk(
  'conversations/listFunctions',
  async ({ conversationId }: { conversationId: string }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/functions`;
    const response = await httpClient.get(url);
    return response.data;
  }
);

export const executeConversationFunction = createAsyncThunk(
  'conversations/executeFunction',
  async ({ 
    conversationId, 
    functionName, 
    functionArguments,
    messageId 
  }: { 
    conversationId: string;
    functionName: string;
    functionArguments?: Record<string, any>;
    messageId?: string;
  }) => {
    const url = `${process.env.REACT_APP_BASE_URL}/comms-api/conversations/${conversationId}/functions/${functionName}`;
    const body: any = {};
    if (functionArguments) body.function_arguments = functionArguments;
    if (messageId) body.message_id = messageId;
    
    const response = await httpClient.post(url, body);
    return response.data;
  }
);

const conversationsSlice = createSlice({
  name: 'conversations',
  initialState,
  reducers: {
    upsert: (state, action) => {
      const newConversation = action.payload;
      const existingConversation = state.entities[newConversation.id];
      
      // If we have an existing conversation with messages, merge them
      if (existingConversation && existingConversation.messages && existingConversation.messages.length > 0) {
        // If the new conversation has fewer or no messages, keep the existing messages
        if (!newConversation.messages || newConversation.messages.length === 0 || 
            (newConversation.messages.length < existingConversation.messages.length && existingConversation._hasFullData)) {
          conversationsAdapter.upsertOne(state, {
            ...newConversation,
            messages: existingConversation.messages,
            _hasFullData: existingConversation._hasFullData
          });
        } else {
          // Otherwise merge messages, removing duplicates
          const existingMessageIds = new Set(existingConversation.messages.map(m => m.id));
          const newMessages = newConversation.messages.filter(m => !existingMessageIds.has(m.id));
          const mergedMessages = sortMessagesByTimestampPriority([...existingConversation.messages, ...newMessages]);
          
          conversationsAdapter.upsertOne(state, {
            ...newConversation,
            messages: mergedMessages,
            _hasFullData: existingConversation._hasFullData || false
          });
        }
      } else {
        // No existing conversation or no messages to preserve
        conversationsAdapter.upsertOne(state, newConversation);
      }
    },
    removeConversation: conversationsAdapter.removeOne,
    toggleOpenConversation: (state, action: PayloadAction<string | null>) => {
      const targetConversationId = action.payload;

      state.openConversationId = targetConversationId;

      // Manage URL parameters
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);

        if (targetConversationId) {
          // Set conversation_id and channel_id in URL
          url.searchParams.set('conversation_id', targetConversationId);
          // Only set channel_id if we can get it from state
          const conversation = state.entities[targetConversationId];
          if (conversation && conversation.channel_id) {
            url.searchParams.set('channel_id', conversation.channel_id);
          }
        } else {
          // Remove conversation_id and channel_id from URL
          url.searchParams.delete('conversation_id');
          url.searchParams.delete('channel_id');
        }

        window.history.replaceState({}, '', url);
      }
    },
    toggleStarredOptimistic: (state, action: PayloadAction<{ conversationId: string; starred: boolean }>) => {
      const { conversationId, starred } = action.payload;
      if (state.entities[conversationId]) {
        state.entities[conversationId].starred = starred;
      }
    },
    markConversationAsRead: (state, action) => {
      const targetConversationId = action.payload;
      const index = state.ids.findIndex(id => id === targetConversationId);
      if (index > -1) {
        state.entities[targetConversationId].read = true;
      }
    },
    addMessage: (state, action) => {
      const message = action.payload;
      const conversationId = message.conversation_id;
      if (state.entities[conversationId]) {
        const messages = state.entities[conversationId].messages || [];
        const messageExists = messages.some(msg => msg.id === message.id);
        if (!messageExists) {
          state.entities[conversationId].messages = sortMessagesByTimestampPriority([...messages, { ...message }]);
        } else {
          // Create a new array with the updated message
          const updatedMessages = messages.map(msg =>
            msg.id === message.id ? { ...message } : msg
          );
          state.entities[conversationId].messages = sortMessagesByTimestampPriority(updatedMessages);
        }
      }
    },
    addMessagesToConversation: (state, action) => {
      const { conversationId, messages } = action.payload;
      if (state.entities[conversationId]) {
        const existingMessages = state.entities[conversationId].messages || [];
        const newMessages = messages.filter(msg => !existingMessages.find(existingMsg => existingMsg.id === msg.id))
          .map(msg => ({ ...msg })); // Create a deep copy of each message
        state.entities[conversationId].messages = sortMessagesByTimestampPriority([...existingMessages, ...newMessages]);
      }
    },
    removeMessageFromConversation: (state, action) => {
      const { conversationId, messageId } = action.payload;
      if (state.entities[conversationId]) {
        state.entities[conversationId].messages = state.entities[conversationId].messages.filter(msg => msg.id !== messageId);
      }
    },
    decrementMessageCount: (state, action) => {
      const { conversationId } = action.payload;
      if (state.entities[conversationId]) {
        state.entities[conversationId].message_count -= 1;
      }
    },
    upsertConversation: (state, action) => {
      const newConversation = action.payload;
      const existingConversation = state.entities[newConversation.id];
      
      // If we have an existing conversation with messages, merge them
      if (existingConversation && existingConversation.messages && existingConversation.messages.length > 0) {
        // If the new conversation has fewer or no messages, keep the existing messages
        if (!newConversation.messages || newConversation.messages.length === 0 || 
            (newConversation.messages.length < existingConversation.messages.length && existingConversation._hasFullData)) {
          conversationsAdapter.upsertOne(state, {
            ...newConversation,
            messages: existingConversation.messages,
            _hasFullData: existingConversation._hasFullData
          });
        } else {
          // Otherwise merge messages, removing duplicates
          const existingMessageIds = new Set(existingConversation.messages.map(m => m.id));
          const newMessages = newConversation.messages.filter(m => !existingMessageIds.has(m.id));
          const mergedMessages = sortMessagesByTimestampPriority([...existingConversation.messages, ...newMessages]);
          
          conversationsAdapter.upsertOne(state, {
            ...newConversation,
            messages: mergedMessages,
            _hasFullData: existingConversation._hasFullData || false
          });
        }
      } else {
        // No existing conversation or no messages to preserve
        conversationsAdapter.upsertOne(state, newConversation);
      }
    },
    removeConversationById: conversationsAdapter.removeOne,
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
    },
    resetPagination(state) {
      state.lastCursor = null;
      // Save current openConversationId
      const currentOpenConversationId = state.openConversationId;
      // Remove all conversations but preserve the structure
      conversationsAdapter.removeAll(state);
      // Restore openConversationId
      state.openConversationId = currentOpenConversationId;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversationsWithMessages.pending, (state, action) => {
        state.loading = true;
        // Only set initialLoad for non-cursor requests
        if (!action.meta.arg.cursor) {
          state.initialLoad = true;
        }
        state.error = null;
      })
      .addCase(fetchConversationsWithMessages.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload || !Array.isArray(action.payload.conversations)) {
          return;
        }

        // Save current openConversationId
        const currentOpenConversationId = state.openConversationId;

        // Process each conversation to preserve existing messages
        const conversationsToUpdate = action.payload.conversations.map(newConv => {
          const existingConv = state.entities[newConv.id];
          
          // If we have an existing conversation with more messages, preserve them
          if (existingConv && existingConv.messages && existingConv.messages.length > 0) {
            // If the new conversation has fewer or no messages, keep the existing messages
            if (!newConv.messages || newConv.messages.length === 0 || 
                (newConv.messages.length < existingConv.messages.length && existingConv._hasFullData)) {
              return {
                ...newConv,
                messages: existingConv.messages,
                _hasFullData: existingConv._hasFullData
              };
            }
            
            // Otherwise merge messages, removing duplicates
            const existingMessageIds = new Set(existingConv.messages.map(m => m.id));
            const newMessages = newConv.messages.filter(m => !existingMessageIds.has(m.id));
            const mergedMessages = sortMessagesByTimestampPriority([...existingConv.messages, ...newMessages]);
            
            return {
              ...newConv,
              messages: mergedMessages,
              _hasFullData: existingConv._hasFullData || false
            };
          }
          
          // No existing conversation or no messages to preserve
          return newConv;
        });

        // Check if this is a cursor-based pagination request (loading more)
        const hasCursor = action.meta.arg.cursor && action.meta.arg.cursor !== null;
        
        if (hasCursor) {
          // For cursor-based pagination, append new conversations instead of replacing
          conversationsAdapter.addMany(state, conversationsToUpdate);
        } else {
          // For initial load or filter changes, replace all conversations
          conversationsAdapter.setAll(state, conversationsToUpdate);
        }

        // Restore openConversationId
        state.openConversationId = currentOpenConversationId;

        // Update pagination state
        state.totalConversations = action.payload.total_count || action.payload.conversations.length;
        state.hasMore = action.payload.has_more || false;
        
        // Update cursor to the timestamp of the last conversation for next page
        const lastConversation = action.payload.conversations[action.payload.conversations.length - 1];
        if (lastConversation) {
          // Use the appropriate timestamp based on what the backend expects
          state.lastCursor = lastConversation.latest_delivery_at || 
                             lastConversation.latest_message_at || 
                             lastConversation.updated_at;
        }
      })
      .addCase(fetchConversationsWithMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.initialLoad = false;
      })
      .addCase(fetchConversationWithMessages.pending, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        if (state.ids.includes(conversationId)) {
          state.entities[conversationId].loading = true;
        }
      })
      .addCase(fetchConversationWithMessages.fulfilled, (state, action) => {
        const conversation = action.payload;
        const conversationId = conversation?.id;
        if (!conversationId) {
          return;
        };
        const existingConversation = state.entities[conversationId];

        // Create a safe copy of the conversation with properly copied message objects
        const safeConversation = {
          ...conversation,
          messages: Array.isArray(conversation.messages)
            ? sortMessagesByTimestampPriority(conversation.messages.map(msg => ({ ...msg })))
            : []
        };

        const updatedConversation = mergeExistingOrNewConversationAndGetUpdatedConversationObject(
          existingConversation,
          safeConversation
        );

        // Filter out marked-for-deletion messages and ensure we're working with copies
        updatedConversation.messages = sortMessagesByTimestampPriority(
          updatedConversation.messages
            .filter(message => !message.marked_to_be_deleted)
            .map(msg => ({ ...msg }))
        );

        conversationsAdapter.upsertOne(state, updatedConversation);
        state.entities[conversation.id].loading = false;
        state.entities[conversation.id].error = null;
        // Mark as having full data when messages are loaded individually via fetchConversationWithMessages
        if (updatedConversation.messages && updatedConversation.messages.length > 3) {
          state.entities[conversation.id]._hasFullData = true;
        }
      })
      .addCase(updateConversationStatus.pending, (state, action) => {
        const conversationId = action?.meta?.arg?.conversationId;
        if (state.ids.includes(conversationId)) {
          state.entities[conversationId].status = 'loading'
        }
      })
      .addCase(updateConversationStatus.fulfilled, (state, action) => {
        const conversation = action.payload.conversation;
        if (state.ids.includes(conversation.id)) {
          state.entities[conversation.id].status = conversation.status;
        }
      })
      .addCase(conversationAutocomplete.pending, (state, action) => {
        const conversationId = action?.meta?.arg?.conversationId;
        if (state.ids.includes(conversationId)) {
          state.entities[conversationId].autocomplete_status = 'loading'
        }
      })
      .addCase(conversationAutocomplete.fulfilled, (state, action) => {
        // const conversation = action.payload.conversation;
        // if (state.ids.includes(conversation.id)) {
        //   state.entities[conversation.id].autocomplete_status = 'queued'
        // }
      })
      .addCase(conversationAutocompleteNow.pending, (state, action) => {
        const conversationId = action?.meta?.arg?.conversationId;
        if (state.ids.includes(conversationId)) {
          state.entities[conversationId].autocomplete_status = 'loading'
        }
      })
      .addCase(conversationAutocompleteNow.fulfilled, (state, action) => {
        // const { conversation_id, message } = action.payload;
        // if (state.ids.includes(conversation_id)) {
        //   const conversation = state.entities[conversation_id];
        //   const messageExists = conversation.messages?.find(msg => msg.id === message.id);
        //   if (!messageExists) {
        //     conversation.messages = [...(conversation.messages || []), message];
        //     conversation.autocomplete_status = 'pending';
        //   }
        // } 
      })
      .addCase(conversationAutocompleteNow.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        const index = state.ids.findIndex(id => id === action.meta.arg.conversationId);
        if (index > -1) {
          state.entities[state.ids[index]].autocomplete_status = 'error'
        }
      })
      .addCase(updateConversationDeliveryDelay.fulfilled, (state, action) => {
        // const conversation = action.payload.conversation;
        // if (state.ids.includes(conversation.id)) {
        //   state.entities[conversation.id].delivery_delay = conversation.delivery_delay;
        // }
      })
      .addCase(updateConversationDeliveryState.pending, (state, action) => {
        const conversationId = action?.meta?.arg?.conversationId;
        if (state.ids.includes(conversationId)) {
          state.entities[conversationId].delivery_state = 'loading'
        }
      })
      .addCase(updateConversationDeliveryState.fulfilled, (state, action) => {
        const conversation = action.payload.conversation;
        if (state.ids.includes(conversation.id)) {
          state.entities[conversation.id].delivery_state = conversation.delivery_state;
        }
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        const conversation = action.payload.conversation;
        if (state.ids.includes(conversation.id)) {
          state.entities[conversation.id] = {
            ...state.entities[conversation.id],
            messages: action.payload.messages
          };
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        // Handle the response structure: { message: "Message posted successfully", data: { ...messageObject } }
        const messageData = action.payload.data;
        if (!messageData || !messageData.conversation_id) {
          console.error('Invalid message data in sendMessage response:', action.payload);
          return;
        }
        
        const conversationId = messageData.conversation_id;
        if (state.ids.includes(conversationId)) {
          const conversation = state.entities[conversationId];
          const messages = conversation.messages || [];
          const messageExists = messages.some(msg => msg.id === messageData.id);
          if (!messageExists) {
            // Add the new message to the conversation
            conversation.messages = sortMessagesByTimestampPriority([...messages, { ...messageData }]);
          } else {
            // Update existing message if it already exists
            const updatedMessages = messages.map(msg =>
              msg.id === messageData.id ? { ...messageData } : msg
            );
            conversation.messages = sortMessagesByTimestampPriority(updatedMessages);
          }
        }
      })
      .addCase(initiateCall.pending, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        if (state.entities[conversationId]) {
          state.entities[conversationId].call_status = 'initiating';
        }
      })
      .addCase(initiateCall.fulfilled, (state, action) => {
        const { conversation_id, call_status } = action.payload;
        if (state.entities[conversation_id]) {
          state.entities[conversation_id].call_status = call_status;
        }
      })
      .addCase(initiateCall.rejected, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        if (state.entities[conversationId]) {
          state.entities[conversationId].call_status = 'failed';
        }
      })
      .addCase(updateConversation.pending, (state, action) => {
        const conversationId = action?.meta?.arg?.conversationId;
        if (state.ids.includes(conversationId)) {
          state.entities[conversationId].status = 'loading';
        }
      })
      .addCase(updateConversation.fulfilled, (state, action) => {
        const updatedConversation = action.payload;
        // find the conversation and update it except for its messages
        const conversation = state.entities[updatedConversation.id];
        if (conversation) {
          // Update all fields except messages to preserve existing messages
          conversation.follow_up = updatedConversation.follow_up;
          conversation.status = updatedConversation.status;
          conversation.delivery_delay = updatedConversation.delivery_delay;
          conversation.memory = updatedConversation.memory;
          conversation.metadata = updatedConversation.metadata;
          conversation.starred = updatedConversation.starred;
          conversation.subject = updatedConversation.subject;
          // Update any other fields that might have changed
          conversation.delivery_state = updatedConversation.delivery_state;
          conversation.latest_message_at = updatedConversation.latest_message_at;
          conversation.latest_delivery_at = updatedConversation.latest_delivery_at;
        }
      })
      .addCase(updateConversation.rejected, (state, action) => {
        console.log("updateConversation.rejected", action);
      })
      .addCase(updateConversationTags.fulfilled, (state, action) => {
        const updatedConversation = action.payload;
        if (state.entities[updatedConversation.id]) {
          // Update the entire conversation object to ensure all fields are in sync
          state.entities[updatedConversation.id] = {
            ...state.entities[updatedConversation.id],
            ...updatedConversation,
            messages: state.entities[updatedConversation.id].messages, // Preserve existing messages
          };
        }
      })
      .addCase(updateConversationTags.rejected, (state, action) => {
        console.error("updateConversationTags.rejected", action);
      })
      .addCase(toggleConversationStarred.fulfilled, (state, action) => {
        const updatedConversation = action.payload;
        if (state.entities[updatedConversation.id]) {
          state.entities[updatedConversation.id].starred = updatedConversation.starred;
        }
      })
      .addCase(toggleConversationStarred.rejected, (state, action) => {
        // Revert optimistic update on failure
        const conversationId = action.meta.arg.conversationId;
        const originalStarred = !action.meta.arg.starred; // Revert to opposite of what was attempted
        if (state.entities[conversationId]) {
          state.entities[conversationId].starred = originalStarred;
        }
        console.error("Failed to toggle starred status:", action.error);
      })
      .addCase(getConversationFollowUp.fulfilled, (state, action) => {
        const { conversationId, followUp } = action.payload;
        if (state.entities[conversationId]) {
          state.entities[conversationId].follow_up = followUp;
        }
      })
      .addCase(getConversationFollowUp.rejected, (state, action) => {
        console.error('Failed to fetch follow-up settings:', action.payload);
      })
      .addCase(updateConversationFollowUp.pending, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        if (state.entities[conversationId]) {
          state.entities[conversationId].loading = true;
        }
      })
      .addCase(updateConversationFollowUp.fulfilled, (state, action) => {
        const { conversationId, followUp } = action.payload;
        if (state.entities[conversationId]) {
          state.entities[conversationId].follow_up = followUp;
          state.entities[conversationId].loading = false;
        }
      })
      .addCase(updateConversationFollowUp.rejected, (state, action) => {
        const conversationId = action.meta.arg.conversationId;
        if (state.entities[conversationId]) {
          state.entities[conversationId].loading = false;
          state.entities[conversationId].error = action.payload || 'Failed to update follow-up settings';
        }
        console.error('Failed to update follow-up settings:', action.payload);
      })
      .addCase(enableConversationFollowUp.pending, (state, action) => {
        const conversationId = action.meta.arg;
        if (state.entities[conversationId]) {
          state.entities[conversationId].loading = true;
        }
      })
      .addCase(enableConversationFollowUp.fulfilled, (state, action) => {
        const { conversationId, followUp } = action.payload;
        if (state.entities[conversationId]) {
          state.entities[conversationId].follow_up = followUp;
          state.entities[conversationId].loading = false;
        }
      })
      .addCase(enableConversationFollowUp.rejected, (state, action) => {
        const conversationId = action.meta.arg;
        if (state.entities[conversationId]) {
          state.entities[conversationId].loading = false;
          state.entities[conversationId].error = action.payload || 'Failed to enable follow-up';
        }
        console.error('Failed to enable follow-up:', action.payload);
      })
      .addCase(disableConversationFollowUp.pending, (state, action) => {
        const conversationId = action.meta.arg;
        if (state.entities[conversationId]) {
          state.entities[conversationId].loading = true;
        }
      })
      .addCase(disableConversationFollowUp.fulfilled, (state, action) => {
        const { conversationId, followUp } = action.payload;
        if (state.entities[conversationId]) {
          state.entities[conversationId].follow_up = followUp;
          state.entities[conversationId].loading = false;
        }
      })
      .addCase(disableConversationFollowUp.rejected, (state, action) => {
        const conversationId = action.meta.arg;
        if (state.entities[conversationId]) {
          state.entities[conversationId].loading = false;
          state.entities[conversationId].error = action.payload || 'Failed to disable follow-up';
        }
        console.error('Failed to disable follow-up:', action.payload);
      })
      .addCase(deleteConversations.fulfilled, (state, action) => {
        const { successfulDeletes } = action.payload;
        conversationsAdapter.removeMany(state, successfulDeletes);
      })
      .addCase(deleteConversations.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete conversations';
      })
      // Fetch Conversations Full Data
      .addCase(fetchConversationsFullData.pending, (state) => {
        // Don't set main loading state, just track that we're loading full data
      })
      .addCase(fetchConversationsFullData.fulfilled, (state, action) => {
        if (!action.payload || !Array.isArray(action.payload.conversations)) {
          return;
        }
        
        // Update existing conversations with full data
        action.payload.conversations.forEach((fullConversation) => {
          if (state.entities[fullConversation.id]) {
            // Merge full data into existing conversation
            const existingConversation = state.entities[fullConversation.id];
            const updatedConversation = {
              ...existingConversation,
              ...fullConversation,
              _hasFullData: true,
              messages: sortMessagesByTimestampPriority(fullConversation.messages || [])
            };
            conversationsAdapter.updateOne(state, {
              id: fullConversation.id,
              changes: updatedConversation
            });
          }
        });
      })
      .addCase(fetchConversationsFullData.rejected, (state, action) => {
        console.error('Failed to fetch full conversation data:', action.error);
      })
      .addCase(listConversationFunctions.fulfilled, (state, action) => {
        // Handle the response from the listConversationFunctions thunk
        // This is a placeholder and should be replaced with the actual implementation
        console.log("Conversation functions:", action.payload);
      })
      .addCase(executeConversationFunction.fulfilled, (state, action) => {
        const response = action.payload;
        const { messageId } = action.meta.arg;
        
        if (!response || !response.response || !response.response.message) {
          console.error("Invalid response from executeConversationFunction:", response);
          return;
        }
        
        const newMessage = response.response.message;
        const conversationId = newMessage.conversation_id;
        
        if (!state.entities[conversationId]) {
          console.error("Conversation not found in state:", conversationId);
          return;
        }
        
        const conversation = state.entities[conversationId];
        const messages = conversation.messages || [];
        
        if (messageId) {
          // Update existing message if messageId was provided
          const messageIndex = messages.findIndex(msg => msg.id === messageId);
          if (messageIndex !== -1) {
            // Merge the new message data with the existing message
            const existingMessage = messages[messageIndex];
            const updatedMessage = {
              ...existingMessage,
              ...newMessage,
              // Preserve the original message ID and merge metadata
              id: existingMessage.id,
              metadata: {
                ...existingMessage.metadata,
                ...newMessage.metadata
              }
            };
            
            // Create new array with updated message
            const updatedMessages = [...messages];
            updatedMessages[messageIndex] = updatedMessage;
            state.entities[conversationId].messages = sortMessagesByTimestampPriority(updatedMessages);
          } else {
            // If message not found, add as new message
            state.entities[conversationId].messages = sortMessagesByTimestampPriority([...messages, newMessage]);
          }
        } else {
          // Add as new message if no messageId provided
          const messageExists = messages.some(msg => msg.id === newMessage.id);
          if (!messageExists) {
            state.entities[conversationId].messages = sortMessagesByTimestampPriority([...messages, newMessage]);
          }
        }
      });
  },
});
export const {
  selectAll: selectAllConversations,
  selectById: selectConversationById,
  selectIds: selectConversationIds,
} = conversationsAdapter.getSelectors<RootState>((state) => state.conversations);

export default conversationsSlice.reducer;
export const { toggleOpenConversation, addMessage, addMessagesToConversation, removeConversation, removeMessageFromConversation, upsertConversation, decrementMessageCount, removeConversationById, setLimit, resetPagination, toggleStarredOptimistic } = conversationsSlice.actions;

// Add new selectors
export const selectHasMore = (state: RootState) => state.conversations.hasMore;
export const selectLastCursor = (state: RootState) => state.conversations.lastCursor;
export const selectLimit = (state: RootState) => state.conversations.limit;
//END /src/features/account/conversations.slice.tsx
